import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { catalogQueryOptions } from "../queries/connections";
import { peopleQueryOptions } from "../queries/people";
import { membersPersonalView } from "./members-personal";
import { ownersComplete, ownersFrom } from "./owners";
import { NO_SESSION, useConnectionsScope } from "./use-connections-scope";

/**
 * The members' personal accounts switch as every Connections screen reads it: from the address
 * (`?members=true`), checked against the catalog, with the members' names for the Owner column.
 * The people read is made only for someone who has the switch, and only they see other people's
 * accounts, so a member's screens never ask for it.
 */
export function useMembersPersonal() {
  const { context } = useConnectionsScope();
  const session = context ?? NO_SESSION;
  const search: { members?: boolean } = useSearch({ strict: false });
  const catalog = useQuery({ ...catalogQueryOptions(session), enabled: Boolean(context) });
  const view = membersPersonalView({
    requested: search.members === true,
    // A catalog that failed to load grants nothing, so the page falls back to the plain lists.
    canView: catalog.isError ? false : catalog.data?.canViewMembersPersonal,
  });
  const people = useQuery({
    ...peopleQueryOptions(session.scope.userId),
    enabled: Boolean(context) && view.available,
  });
  const owners = useMemo(() => ownersFrom(people.data), [people.data]);
  // False until the people read has answered with every member's user ID, so a failed read, or an
  // older identity service that sends none, never turns a current member into a "former" one.
  const ownersKnown = ownersComplete(people.data);
  return { ...view, owners, ownersKnown, people };
}
